import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { PosSearchBox } from 'src/core/POS/PosSearchBox';
import { useDebouncedValueSafe } from 'src/hooks/useDebouncedValueSafe';
import { ContentId } from 'src/utils/constants/contentId';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';

export const EventEntitySearchBox = <
  TQuery extends QueryWithViewMode & { searchText?: string | null },
>({
  onIdLookup,
  minWidth = 250,
  maxWidth,
  disabled,
  searchText,
  setSearchText,
  placeholderContentId = ContentId.SearchEventOrLookupId,
  disableSearchMode,
}: {
  onIdLookup: (id: string) => void;
  minWidth?: number | string;
  maxWidth?: number | string;
  disabled?: boolean;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  placeholderContentId?: ContentId;
  disableSearchMode?: boolean;
}) => {
  const searchPlaceholder = useContent(placeholderContentId);

  const { filterQuery, tempQuery, setFilterQuery } =
    useFilterQueryContext<TQuery>();
  const queriesRef = useRef({ tempQuery, filterQuery });
  queriesRef.current = { tempQuery, filterQuery };
  const onSearchTextChange = useCallback(
    (text: string) => {
      const query = { ...queriesRef.current.tempQuery };
      // Clear query
      if (text == null || text.length === 0) {
        Object.assign(query, {
          searchText: null,
          entityIds: null,
          marketplaceEntityIds: null,
        });
        setFilterQuery(query);
      } else if (text !== queriesRef.current.filterQuery.searchText) {
        Object.assign(query, {
          searchText: text || null,
        });
        setFilterQuery(query);
      }
    },
    [setFilterQuery]
  );

  const [debouncedSearchValue] = useDebouncedValueSafe(searchText);

  // Flag to avoid initial update
  const hasInputRef = useRef(false);
  useEffect(() => {
    if (
      hasInputRef.current &&
      debouncedSearchValue != null &&
      !disableSearchMode
    ) {
      onSearchTextChange(debouncedSearchValue);
    }
  }, [debouncedSearchValue, disableSearchMode, onSearchTextChange]);

  const onSearchChange = useCallback(
    (value: string) => {
      hasInputRef.current = true;
      setSearchText(value);
    },
    [setSearchText]
  );

  return (
    <PosSearchBox
      value={searchText}
      disabled={disabled}
      minWidth={minWidth}
      maxWidth={maxWidth}
      placeholder={searchPlaceholder}
      onSearchChange={onSearchChange}
      onEnterKeyPressed={(curValue) => {
        hasInputRef.current = true;
        onIdLookup(curValue);

        // If user press enter - they're interested in look-up, not text-search, so clear the search
        // tex so the tables doesn't get empty while the modal opens
        // But only if it's a single-id lookup, if multiple, we keep the search filter because
        // the table needs to display the result set
        if (!curValue.includes(',')) {
          onSearchTextChange('');
        }
      }}
    />
  );
};
